import {
  IQuestionRequest,
  ISurveyBuilderQuestion,
  ISurveyBuilderSurvey,
  ISurveyBuilderVersion,
} from "../@types/SurveyBuilder";
import { ISurvey, ISurveyQuestion } from "../@types/Surveys";

/**
 * EN-5261: the builder shows one flat list of questions. Sections stay in the database (each
 * version normally has a single one); their order decides the flat order.
 */
export function flattenQuestions(
  version: ISurveyBuilderVersion
): ISurveyBuilderQuestion[] {
  return [...version.sections]
    .sort((a, b) => a.order - b.order)
    .flatMap((section) =>
      [...section.questions].sort((a, b) => a.order - b.order)
    );
}

/** The version's single section to add questions to (the last one, if an import left several). */
export function targetSectionId(version: ISurveyBuilderVersion) {
  const sections = [...version.sections].sort((a, b) => a.order - b.order);
  return sections[sections.length - 1]?.id;
}

/**
 * The reorder request for a new flat order: every question goes into the first section and any
 * other section is left empty, to be deleted afterwards. That is how a survey imported with
 * several sections becomes a single-section one, the first time its draft is reordered.
 */
export function singleSectionLayout(
  version: ISurveyBuilderVersion,
  orderedQuestionIds: string[]
) {
  const sections = [...version.sections].sort((a, b) => a.order - b.order);
  const [first, ...rest] = sections;

  return {
    layout: [
      { sectionId: first.id, questionIds: orderedQuestionIds },
      ...rest.map((section) => ({ sectionId: section.id, questionIds: [] })),
    ],
    emptySectionIds: rest.map((section) => section.id),
  };
}

export function toEditorQuestion(
  question: ISurveyBuilderQuestion
): ISurveyQuestion {
  return {
    id: question.id,
    order: question.order,
    text: question.text ?? "",
    description: question.description ?? "",
    mandatory: question.mandatory,
    type: question.type,
    nextQuestionId: question.nextQuestionId,
    domain: question.domain,
    code: question.code,
    options: question.options.map((option) => ({
      id: option.id,
      label: option.label ?? "",
      value: option.value,
      nextQuestionId: option.nextQuestionId,
      code: option.code,
    })),
    attributes: question.attributes.map((attribute) => ({ ...attribute })),
  };
}

export function toQuestionRequest(
  question: ISurveyQuestion,
  languageCode: string,
  rowVersion?: number
): IQuestionRequest {
  return {
    languageCode,
    type: question.type,
    text: question.text?.trim(),
    description: question.description?.trim(),
    mandatory: question.mandatory,
    // The editor uses "" for "no jump" / "no domain"; the API wants them absent.
    nextQuestionId: question.nextQuestionId || undefined,
    domain: question.domain || undefined,
    code: question.code || undefined,
    options: (question.options ?? []).map((option) => ({
      id: option.id,
      label: option.label,
      value: Number(option.value),
      nextQuestionId: option.nextQuestionId || undefined,
      code: option.code || undefined,
    })),
    attributes: (question.attributes ?? []).filter(
      (attribute) => !(attribute.name === "Intervals" && attribute.value === "")
    ),
    rowVersion,
  };
}

/** The version as the answering screen renders it, for the Preview tab. */
export function toPreviewSurvey(survey: ISurveyBuilderSurvey): ISurvey {
  const { version } = survey;

  return {
    id: survey.id,
    surveyVersionId: version.id,
    name: version.name ?? "",
    description: version.description ?? "",
    type: survey.type,
    code: survey.code,
    scoreStrategy: version.scoreStrategy,
    displayRules: version.displayRules,
    sections: [
      {
        order: 0,
        title: "",
        questions: flattenQuestions(version).map(toEditorQuestion),
      },
    ],
  };
}
